import { useQueryClient } from '@tanstack/react-query';
import Router from 'next/router';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type GetBoardResponse } from '@air/api';
import { type Board } from '@air/api/types';

import { type ExpandableSectionId } from '~/components/AssetModalV2/InfoPanel/CollapsibleSectionsContextProvider';
import { getPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { createBoardClipRoute } from '~/constants/routes';
import { QueryParamNames, type QueryType } from '~/constants/search';
import { type AssetModalPanel } from '~/providers/AssetModalPanelContextProvider';
import { setCentralizedBoardAction, setCentralizedBoardIdAndTitleAction } from '~/store/centralizedBoard/actions';
import { setCentralizedClipAction } from '~/store/centralizedClip/actions';
import { type LocationBoard } from '~/store/router/types';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { type GoToAssetPageProps } from './useGoToAssetPage';

export function useGoToBoardAssetPage() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const goToBoardAssetPage = useCallback(
    ({
      asset,
      board,
      tab,
      expandedSections,
      query: additionalQuery,
    }: {
      board: LocationBoard | Board;
      asset: GoToAssetPageProps['asset'];
      tab?: AssetModalPanel;
      expandedSections?: ExpandableSectionId[];
      query?: QueryType;
    }) => {
      const centralizedBoardId = getBoardIdFromPath(window.location.pathname);

      if (centralizedBoardId !== board.id) {
        const cachedBoard = queryClient.getQueryData<GetBoardResponse>(getPrivateBoardKey({ boardId: board.id }));

        if (cachedBoard) {
          dispatch(setCentralizedBoardAction({ board: cachedBoard }));
        } else {
          dispatch(setCentralizedBoardIdAndTitleAction({ board }));
        }
      }

      const query: Record<string, string | string[] | null | undefined> = {};

      if (tab) {
        query.tab = tab;
      }

      if (expandedSections?.length) {
        query[QueryParamNames.expandedSections] = expandedSections.join(',');
      }

      if (additionalQuery) {
        Object.assign(query, additionalQuery);
      }

      dispatch(setCentralizedClipAction({ clip: asset }));
      Router.push(
        {
          pathname: createBoardClipRoute({ clipId: asset.id, boardTitle: board.title, boardId: board.id }),
          query: Object.keys(query).length > 0 ? query : undefined,
        },
        undefined,
        { shallow: true },
      );
    },
    [dispatch, queryClient],
  );

  return {
    goToBoardAssetPage,
  };
}
